import { CHANNELS } from "../../shared/channels";
import { RoutineSchedule, RoutineStatus } from "../../shared/types";
import { call } from "../bridge";
import { cloneTemplate, fillFields, flashToast } from "../dom";
import { t } from "../i18n";

// one routine row with a tick, shared by the routines screen and today

export function localDate(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const WEEKDAY_NAMES = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// the server sends an english label, this builds the same words in the picked language
function scheduleText(schedule: RoutineSchedule): string {
  switch (schedule.kind) {
    case "weekdays":
      return schedule.days.length === 5 && !schedule.days.includes(0) && !schedule.days.includes(6)
        ? t("weekdays")
        : schedule.days.map((day) => t(WEEKDAY_NAMES[day])).join(" ");
    case "every":
      return schedule.days === 1 ? t("daily") : t("every {n} days", { n: schedule.days });
    case "perWeek":
      return t("{n}x a week", { n: schedule.times });
    case "monthly":
      return t("monthly on day {day}", { day: schedule.day });
    default:
      return t("daily");
  }
}

function metaText(item: RoutineStatus): string {
  const parts = [item.routine.schedule ? scheduleText(item.routine.schedule) : item.label];
  if (item.routine.schedule.kind === "perWeek") parts.push(t("{done}/{times} this week", { done: item.weekCount, times: item.routine.schedule.times }));
  if (item.routine.remindAt) parts.push(t("ping {time}", { time: item.routine.remindAt }));
  if (item.routine.paused) parts.push(t("paused"));
  if (item.routine.note) parts.push(item.routine.note);
  return parts.join(" · ");
}

// with no onOpen it's someone else's routine, shown but not ticked or opened
export function buildRoutineItem(item: RoutineStatus, onOpen: ((item: RoutineStatus) => void) | null): HTMLElement {
  const unit = item.routine.schedule.kind === "perWeek";
  const row = fillFields(cloneTemplate("template-routine"), {
    title: item.routine.title,
    meta: metaText(item),
    streak: item.streak > 1 ? (unit ? t("{n}w streak", { n: item.streak }) : t("{n} streak", { n: item.streak })) : ""
  });
  row.classList.toggle("done", item.done);
  row.classList.toggle("off", !item.due && !item.done);
  row.classList.toggle("paused", item.routine.paused);
  const check = row.querySelector<HTMLButtonElement>("[data-action='check']")!;
  if (!onOpen) {
    check.disabled = true;
    return row;
  }
  row.addEventListener("click", () => onOpen(item));
  check.addEventListener("click", async (event) => {
    event.stopPropagation();
    try {
      await call(CHANNELS.routinesDone, item.routine.id, !item.done, localDate());
      if (!item.done) flashToast(t("{title} done", { title: item.routine.title }));
    } catch (error) {
      flashToast(error instanceof Error ? error.message : String(error));
    }
  });
  return row;
}
